import { Avatar, FormCard } from '@lmc/ui-kit'
import { Text } from '@lmc/ui-kit/src/general'
import Divider from '@lmc/ui-kit/src/layout/Divider/Divider'
import dayjs from 'dayjs'
import { NAMESPACE } from 'locale'
import { useTranslation } from 'react-i18next'
import styled from 'styled-components'
import type { StockRecord } from '../../types'

type AddStockDetailContentProps = {
  record: StockRecord
  medicationName: string
}

export const AddStockDetailContent = ({
  record
}: AddStockDetailContentProps) => {
  const { t } = useTranslation(NAMESPACE.EMAR_V2)

  const description = Array.isArray(record.notes)
    ? record.notes.join(', ')
    : record.notes || ''
  const previousExpectedStock =
    (record.expectedStock ?? 0) - record.quantityChange
  const receivedStock = record.quantityChange
  const expectedStock = record.expectedStock
  const stockUnit = record.unit || ''

  const receivedByName = record.receivedBy
    ? typeof record.receivedBy.name === 'object'
      ? `${record.receivedBy.name?.first || ''} ${record.receivedBy.name?.last || ''}`.trim()
      : record.receivedBy.name || ''
    : ''
  const receivedDateFormatted = record.actionTime
    ? dayjs(record.actionTime).format('DD-MM-YYYY')
    : '-'
  const receivedFrom = record.receivedFrom || ''

  const timeLogged = record.date
    ? dayjs(record.date).format('DD-MM-YYYY HH:mm')
    : '-'
  const carerName =
    typeof record.recordedBy?.name === 'object'
      ? `${record.recordedBy?.name?.first || ''} ${record.recordedBy?.name?.last || ''}`.trim()
      : record.recordedBy?.name || ''

  return (
    <SectionContainer>
      <FormCard level={2}>
        <InfoRow>
          <FullWidthSection>
            <StyledLabel level={2}>{t('DESCRIPTION')}</StyledLabel>
            <Text>{description || '-'}</Text>
          </FullWidthSection>
        </InfoRow>
        <RowDivider />
        <InfoRow>
          <InfoSection>
            <StyledLabel level={2}>{t('PREVIOUS_EXPECTED_STOCK')}</StyledLabel>
            <Text weight="bold">
              {previousExpectedStock !== undefined
                ? `${previousExpectedStock} ${stockUnit}`
                : '-'}
            </Text>
          </InfoSection>
          <InfoSection>
            <StyledLabel level={2}>{t('RECEIVED_STOCK')}</StyledLabel>
            <Text weight="bold">
              {receivedStock !== undefined
                ? `${receivedStock} ${stockUnit}`
                : '-'}
            </Text>
          </InfoSection>
          <InfoSection>
            <StyledLabel level={2}>{t('EXPECTED_STOCK')}</StyledLabel>
            <Text weight="bold">
              {expectedStock !== undefined
                ? `${expectedStock} ${stockUnit}`
                : '-'}
            </Text>
          </InfoSection>
        </InfoRow>
        <InfoRow>
          <InfoSection>
            <StyledLabel level={2}>{t('RECEIVED_BY')}</StyledLabel>
            {record.receivedBy ? (
              <ReceivedByContainer>
                <Avatar
                  url={(record.receivedBy as any).picture as unknown as string}
                  size={16}
                />
                <Text weight="bold">{receivedByName || '-'}</Text>
              </ReceivedByContainer>
            ) : (
              <Text weight="bold">-</Text>
            )}
          </InfoSection>
          <InfoSection>
            <StyledLabel level={2}>{t('RECEIVED_DATE')}</StyledLabel>
            <Text weight="bold">{receivedDateFormatted || '-'}</Text>
          </InfoSection>
          <InfoSection>
            <StyledLabel level={2}>{t('RECEIVED_FROM')}</StyledLabel>
            <Text weight="bold">{receivedFrom || '-'}</Text>
          </InfoSection>
        </InfoRow>
        <RowDivider />
        <InfoRow>
          <InfoSection>
            <StyledLabel level={2}>{t('TIME_RECORDED')}</StyledLabel>
            <Text>{timeLogged}</Text>
          </InfoSection>
          <InfoSection>
            <StyledLabel level={2}>{t('RECORDED_BY')}</StyledLabel>
            <RecordedByContainer>
              <Avatar
                url={record.recordedBy?.picture as unknown as string}
                size={16}
              />
              <Text>{carerName || '-'}</Text>
            </RecordedByContainer>
          </InfoSection>
          {record.witnessed &&
            record.witnesses &&
            record.witnesses.length > 0 && (
              <InfoSection>
                <StyledLabel level={2}>{t('WITNESSED_BY')}</StyledLabel>
                <WitnessesContainer>
                  {record.witnesses.map((witness: any, index: number) => {
                    return (
                      <WitnessItem key={witness.userId || index}>
                        <Avatar
                          url={witness.picture as unknown as string}
                          size={16}
                        />
                        <Text weight="bold">{witness.name || '-'}</Text>
                      </WitnessItem>
                    )
                  })}
                </WitnessesContainer>
              </InfoSection>
            )}
        </InfoRow>
      </FormCard>
    </SectionContainer>
  )
}

const InfoRow = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  gap: 24px;
`

const InfoSection = styled.div`
  display: flex;
  flex-direction: column;
  width: 184px;
`

const FullWidthSection = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
`

const StyledLabel = styled(Text)`
  color: ${({ theme }) => theme.color.neutral.color5};
`

const ReceivedByContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`

const SectionContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`

const RowDivider = styled(Divider)`
  margin: -8px 0;
`

const RecordedByContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`

const WitnessesContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`

const WitnessItem = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`
